import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { AdminAppShell } from "../../../design-system/shells/AdminAppShell";
import { useAuth } from "../../../lib/auth";
import { api } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import StatusChip from "../../../components/StatusChip";
import { MediaCard } from "../../../components/portfolio/MediaCard";
import { CommentsPanel } from "../../../components/portfolio/CommentsPanel";
import { Section } from "../../../components/portfolio/Section";
import { usePortfolioProject } from "./usePortfolioProject";
import { errMsg } from "../../../lib/portfolio";

function Decision({ projectId, onDone }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/admin/portfolio/projects/${projectId}/decisions`, { decision, body });
      toast.success(decision === "approve" ? "Approved — the Founder decides when to publish." : "Changes requested.");
      setBody("");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="portfolio-decision-panel">
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} maxLength={4000}
        aria-label="Note for the Founder" placeholder="Optional note for the Founder" className="bg-white text-erf-dark"
        data-testid="portfolio-decision-note" />
      <div className="flex flex-wrap gap-2">
        <Button disabled={busy} onClick={() => decide("approve")} className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover"
          data-testid="portfolio-decision-approve">Approve</Button>
        <Button disabled={busy} variant="outline" onClick={() => decide("request_changes")} className="border-erf-borderCream text-erf-dark"
          data-testid="portfolio-decision-request-changes">Request changes</Button>
      </div>
    </div>
  );
}

export default function PortfolioReview() {
  const { id } = useParams();
  const { user } = useAuth();
  const { project, media, error, reload } = usePortfolioProject(id);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (project?.my_role === "reviewer") api.post(`/admin/portfolio/projects/${id}/reviewers/accept`).catch(() => {});
  }, [project?.my_role, id]);
  const refresh = () => { reload(); setTick((t) => t + 1); };
  const reviewer = project?.my_role === "reviewer";
  return (
    <AdminAppShell activeNav="portfolio" title={project ? `Review: ${project.title}` : "Review project"} user={user}
      subtitle="You can view, comment, and approve or request changes. Publishing is up to the Founder."
      breadcrumbs={[{ label: "Portfolio Manager", to: "/admin/portfolio" }, { label: "Review", to: null }]}>
      {error && <p className="text-sm font-nav text-[#991B1B]" role="alert" data-testid="portfolio-review-error">{error}</p>}
      {project && (
        <div className="space-y-5" data-testid="portfolio-review">
          <Section title="Project" testId="portfolio-review-summary"
            actions={<StatusChip status={project.status} testId="portfolio-review-status" />}>
            <p className="text-sm font-nav text-erf-dark">{project.summary || "No summary yet."}</p>
            {project.long_description && <p className="text-sm font-nav text-erf-dark/80 whitespace-pre-wrap">{project.long_description}</p>}
            <p className="text-xs font-nav text-erf-dark/70">{[project.category, project.address_city, project.address_state].filter(Boolean).join(" · ")}</p>
          </Section>
          {reviewer && project.status === "in_review" && (
            <Section title="Your decision" testId="portfolio-review-decision-section"><Decision projectId={id} onDone={refresh} /></Section>
          )}
          {reviewer && project.status !== "in_review" && (
            <p className="text-sm font-nav text-erf-dark" data-testid="portfolio-review-not-open">This project is not waiting for review right now.</p>
          )}
          <Section title="Photos and videos" testId="portfolio-review-media" hint="AI drafts are suggestions only until the Founder approves them.">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {media.map((m) => <MediaCard key={m.id} media={m} founder={false} onChanged={refresh} />)}
            </div>
            {media.length === 0 && <p className="text-sm font-nav text-erf-dark/70">No files yet.</p>}
          </Section>
          <Section title="Comments" testId="portfolio-review-comments"><CommentsPanel projectId={id} reloadKey={tick} /></Section>
        </div>
      )}
    </AdminAppShell>
  );
}
